<script setup lang="ts">
import type { FormInputProps, TextInputTypes } from '~kui/types/input';

import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';
import { getInputStateClassesV2, useCharacterCount } from '~kui/composables/useInputState';
import { useI18n } from 'vue-i18n';

interface Props extends FormInputProps {
  id?: string;
  type?: TextInputTypes;
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  autocomplete?: string;
  dataTestId?: string;
  maxLength?: number;
  errorMessages?: string[];
}

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  type: 'text',
  placeholder: undefined,
  name: undefined,
  disabled: false,
  autocomplete: 'off',
  dataTestId: undefined,
  id: undefined,
  maxLength: 300,
  errorMessages: () => [],
});

const value = defineModel<string | number>({ required: false });

const stringValue = computed(() => (typeof value.value === 'string' ? value.value : ''));
const { enforceCharacterLimit } = useCharacterCount(stringValue, props.maxLength);

const handleCharacterLimit = () => {
  if (typeof value.value === 'string' && value.value.length > props.maxLength) {
    value.value = enforceCharacterLimit(value.value);
  }
};

watch(
  () => value.value,
  () => {
    handleCharacterLimit();
  },
);

const elementId = useId();

const hasError = computed(() => props.errorMessages.length > 0);
const errorsId = computed(() => `error-${elementId}`);

const { t } = useI18n();

// A password field carries its own show/hide control; the input keeps its declared type so the
// browser and password managers still treat the field as a password once it is hidden again.
const isPassword = computed(() => props.type === 'password');
const revealed = ref(false);
const inputType = computed(() => (isPassword.value && revealed.value ? 'text' : props.type));

const { wrapperClasses, elementClasses, fontClasses } = getInputStateClassesV2({
  disabled: computed(() => props.disabled),
  state: computed(() => (hasError.value ? 'error' : props.inputTypeState)),
});

// Callers pass either `data-test-id` (matches the `dataTestId` prop) or the more common
// plain `data-testid` (which Vue does NOT match to `dataTestId` — hyphenate('dataTestId')
// is 'data-test-id', not 'data-testid' — so it lands in $attrs untouched instead). Resolve
// whichever was actually provided so the explicit rebind below can't clobber the $attrs one
// with an unset prop.
const attrs = useAttrs();
const resolvedTestId = computed(() => props.dataTestId ?? (attrs['data-testid'] as string | undefined));

// The explicit bindings below sit after `v-bind="$attrs"`, so an unset one would otherwise wipe a
// caller-supplied value. A wrapper that owns its own message element keeps its describedby/invalid
// whenever this input has no error of its own.
const resolvedAriaInvalid = computed(() =>
  hasError.value || props.inputTypeState === 'error' ? 'true' : (attrs['aria-invalid'] as string | undefined),
);
const resolvedAriaDescribedby = computed(() =>
  hasError.value ? errorsId.value : (attrs['aria-describedby'] as string | undefined),
);

const inputRef = useTemplateRef<{ focus: () => void }>('inputRef');

const focus = () => {
  inputRef.value?.focus();
};

defineExpose({
  focus,
});
</script>

<template>
  <div class="group/kui-input">
    <KUICoreInputLabel
      v-if="!hideLabel && label"
      :label="label"
      :for="id ?? elementId"
      :required="required"
      class="mb-2"
    />
    <div class="relative" :class="wrapperClasses">
      <KUICoreInput
        :id="id ?? elementId"
        ref="inputRef"
        v-bind="$attrs"
        v-model="value"
        :aria-label="hideLabel && label ? label : $attrs['aria-label']"
        :type="inputType"
        :name="name"
        :placeholder="placeholder"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :data-testid="resolvedTestId"
        :aria-required="required"
        :aria-invalid="resolvedAriaInvalid"
        :aria-describedby="resolvedAriaDescribedby"
        :maxlength="maxLength"
        :spellcheck="isPassword ? 'false' : undefined"
        :autocorrect="isPassword ? 'off' : undefined"
        :autocapitalize="isPassword ? 'none' : undefined"
        :class="[fontClasses, elementClasses, { 'pr-12': isPassword }]"
      />
      <button
        v-if="isPassword"
        type="button"
        class="absolute inset-y-0 right-0 flex items-center px-4 text-muted-foreground hover:text-foreground disabled:pointer-events-none"
        :disabled="disabled"
        :aria-label="revealed ? t('kui.password.hide') : t('kui.password.show')"
        :aria-pressed="revealed"
        :aria-controls="id ?? elementId"
        :data-testid="resolvedTestId ? `${resolvedTestId}-toggle` : undefined"
        @click="revealed = !revealed"
      >
        <Icon :name="revealed ? ICONS.eyeOff : ICONS.eye" size="sm" />
      </button>
    </div>
    <KUICoreInputErrors
      v-if="hasError"
      :id="errorsId"
      :data-testid="`error-message-${elementId}`"
      :error-messages="errorMessages"
    />
  </div>
</template>
